// BinaryDocPane — the read surface for a non-markdown document in the
// Documents library. Spreadsheets / PDFs / DOCX share the explorer and
// revision history with living markdown, but they are not opened in the
// markdown editor. Preview matches the templates tab: PDF bytes in a
// native iframe, docx/xlsx/csv as BFF HTML in a sandboxed iframe. Review
// status and comments ride PATCH /docs/{id}/status and /docs/{id}/comments.
// Download and PDF preview go through GET /api/ontology/docs/:id/download
// (current revision: Postgres sidecar, then leftover `s3_key`).

import { useState } from 'react'
import { Download, FileSpreadsheet, History, Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import {
  docsHubApi,
  ingestMetaFromDoc,
  downloadLibraryDoc,
  isDemoMode,
  useAppDispatch,
  useUpdateDocStatusMutation,
  type Doc,
  type DocRevisionMeta,
  type DocStatus
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import DocComments from './DocComments'
import DocFilePreview from './DocFilePreview'
import IngestionDetails from './IngestionDetails'
import { downloadName, formatBytes, statusActions, statusMeta } from './doc-helpers'
import { buildProvenanceLine, clockTime, describeLease } from './doc-helpers'

const DOC_STATUSES: ReadonlySet<string> = new Set(['draft', 'in_review', 'approved', 'published'])

function asDocStatus(value: string | undefined): DocStatus {
  return value !== undefined && DOC_STATUSES.has(value) ? (value as DocStatus) : 'draft'
}

export interface BinaryDocPaneProps {
  doc: Doc
  sourcePage?: number
  revisions: readonly DocRevisionMeta[]
  hasMoreRevisions: boolean
  historyOpen: boolean
  onUpdateWithAgent: () => void
  onToggleHistory: () => void
}

const BinaryDocPane = ({
  doc,
  sourcePage,
  revisions,
  hasMoreRevisions,
  historyOpen,
  onUpdateWithAgent,
  onToggleHistory
}: BinaryDocPaneProps) => {
  const dispatch = useAppDispatch()
  const canWrite = !isDemoMode()
  const [downloading, setDownloading] = useState(false)
  const [updateDocStatus, statusState] = useUpdateDocStatusMutation()
  const status = asDocStatus(doc.status)
  const meta = statusMeta(status)
  const actions = canWrite ? statusActions(status) : []
  const lease = describeLease(doc)
  const fileName = downloadName(doc.name, doc.format)
  const ingestMeta = ingestMetaFromDoc(doc)

  const handleDownload = async (): Promise<void> => {
    setDownloading(true)
    try {
      await downloadLibraryDoc(doc.id, fileName)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  const handleStatus = async (to: DocStatus): Promise<void> => {
    try {
      const updated = await updateDocStatus({ id: doc.id, input: { status: to } }).unwrap()
      dispatch(
        docsHubApi.util.updateQueryData('listDocs', undefined, (draft) => {
          const row = draft.find((item) => item.id === doc.id)
          if (row) row.status = updated.status
        })
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update review status')
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex shrink-0 flex-col gap-2 border-b border-line px-4 py-2.5">
        <div className="flex items-center gap-3">
          <FileSpreadsheet className="size-4 shrink-0 text-ink-3" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-ink">{doc.name}</div>
            <div className="truncate font-mono text-11 text-ink-3">/{doc.path}</div>
          </div>
          <Button size="sm" variant="outline" onClick={onUpdateWithAgent}>
            <Sparkles aria-hidden />
            Update with agent
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={downloading}
            onClick={() => void handleDownload()}
          >
            {downloading ? (
              <Loader2 className="animate-spin" aria-hidden />
            ) : (
              <Download aria-hidden />
            )}
            Download
          </Button>
          <Button
            size="icon-sm"
            variant={historyOpen ? 'default' : 'ghost'}
            aria-label="Version history"
            aria-pressed={historyOpen}
            title="Version history"
            onClick={onToggleHistory}
          >
            <History aria-hidden />
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-11 text-ink-3">
          <Badge variant={meta.variant}>{meta.label}</Badge>
          {doc.format ? (
            <Badge variant="outline" className="uppercase">
              {doc.format}
            </Badge>
          ) : null}
          <span>{formatBytes(doc.size_bytes)}</span>
          <span>{buildProvenanceLine(revisions, { hasMore: hasMoreRevisions })}</span>
          {lease.state !== 'free' ? (
            <span>
              {lease.holderName} holds the edit lock until {clockTime(lease.expiresAt)}
            </span>
          ) : null}
        </div>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden bg-bg-sunk/40">
        <DocFilePreview
          docId={doc.id}
          name={doc.name}
          format={doc.format}
          sourcePage={sourcePage}
        />
      </div>
      <div className="max-h-[40%] shrink-0 overflow-auto border-t border-line px-6 py-4">
        <div className="flex max-w-[40rem] flex-col gap-5">
          {ingestMeta !== null ? <IngestionDetails meta={ingestMeta} /> : null}
          <div className="flex flex-col gap-2">
            <div className="text-11 uppercase tracking-wider text-ink-3">Review status</div>
            <div className="flex flex-wrap items-center gap-2">
              {actions.length === 0 ? (
                <span className="text-sm text-ink-3">
                  {status === 'published'
                    ? 'Published — this document is final.'
                    : canWrite
                      ? 'No further transitions available.'
                      : 'Review actions are browse-only in this guided demo.'}
                </span>
              ) : (
                actions.map((action) => (
                  <Button
                    key={action.to}
                    size="sm"
                    variant={
                      action.to === 'draft' || action.to === 'in_review' ? 'outline' : 'default'
                    }
                    disabled={statusState.isLoading}
                    onClick={() => void handleStatus(action.to)}
                  >
                    {statusState.isLoading ? (
                      <Loader2 className="animate-spin" aria-hidden />
                    ) : null}
                    {action.label}
                  </Button>
                ))
              )}
            </div>
          </div>
          <div className="border-t border-line pt-4">
            <DocComments docId={doc.id} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default BinaryDocPane
